import React, { useCallback, useEffect, useMemo } from 'react';
import { FixedSizeList } from 'react-window';
import { useTable, useFlexLayout, useRowSelect, useSortBy } from 'react-table';
import classNames from 'classnames';
import InfiniteLoader from 'react-window-infinite-loader';
import { ReactWindowScroller } from 'react-window-scroller';
import mergeRefs from 'react-merge-refs';
import IndeterminateCheckbox from 'components/IndeterminateCheckbox/IndeterminateCheckbox';
import { Icon, Loader } from 'semantic-ui-react';
import './VirtualTable.scss';

export default function VirtualTable({
  loading,
  data,
  columns,
  tableClassNames,
  selectable = false,
  disableWindowScroll = false,
  height = 168,
  itemSize = 42,
  autoResetSelectedRows = false,
  hasNextPage,
  lastResetSelectedRows = null,
  displayHeaderSelect = true,
  initialState = {},
  rowClassNames = () => {},
  onRowSelect = () => {},
  onSort = () => {},
  onLoadMoreItems = () => {},
  toggleAllDisabledFunction = () => false,
  toggleRowDisabledFunction = () => false,
}) {
  const selectablePlugins = [
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          style: { width: 45 },
          maxWidth: 1,
          Header: ({ getToggleAllRowsSelectedProps, ...rest }) =>
            displayHeaderSelect && (
              <IndeterminateCheckbox
                {...getToggleAllRowsSelectedProps()}
                disabled={toggleAllDisabledFunction(rest.data)}
              />
            ),
          Cell: ({ row }) => (
            <IndeterminateCheckbox
              {...row.getToggleRowSelectedProps()}
              disabled={toggleRowDisabledFunction(row.original)}
            />
          ),
        },
        ...columns,
      ]);
    },
    useRowSelect,
  ];

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: data || [],
      defaultColumn: { width: 100 },
      autoResetSelectedRows,
      manualSortBy: true,
      initialState,
    },
    useSortBy,
    useFlexLayout,
    ...(selectable ? selectablePlugins : []),
  );

  useEffect(() => {
    onSort(sortBy[0]);
  }, [onSort, sortBy]);

  useEffect(() => {
    if (!lastResetSelectedRows) return;
    if (selectable) {
      toggleAllRowsSelected(false);
    }
  }, [selectable, lastResetSelectedRows, toggleAllRowsSelected]);

  const tableHasScroll = useMemo(
    () => rows.length > height / itemSize,
    [rows, height, itemSize],
  );

  const getColumnStyle = useCallback((column) => {
    return {
      ...column.style,
      justifyContent:
        column?.textAlign === 'right'
          ? 'flex-end'
          : column?.textAlign === 'center'
          ? 'center'
          : 'flex-start',
    };
  }, []);

  const itemCount = hasNextPage ? rows.length + 1 : rows.length;

  const renderRow = useCallback(
    ({ index, style }) => {
      const row = rows[index];
      if (!row) return null;
      prepareRow(row);

      return (
        <div
          {...row.getRowProps({
            style,
          })}
          className={classNames('virtual-table__tr', rowClassNames(row))}
        >
          {row.cells.map((cell, index) => (
            <div
              {...cell.getCellProps([
                {
                  className: classNames(
                    'virtual-table__td',
                    cell.column.className,
                  ),
                  style: getColumnStyle(cell.column),
                },
              ])}
            >
              {cell.render('Cell')}
            </div>
          ))}
        </div>
      );
    },
    [prepareRow, rows, getColumnStyle, rowClassNames],
  );

  useEffect(() => {
    if (selectable) {
      onRowSelect(selectedFlatRows?.map((item) => item.original));
    }
    // eslint-disable-next-line
  }, [selectedFlatRows]);

  useEffect(() => {
    const tableHeader = document.querySelector('.virtual-table__head');
    const tableBody = document.querySelector('.virtual-table__body-rows');
    const handleScroll = (event) => {
      tableHeader.scrollTo({
        left: event.target.scrollLeft,
      });
    };
    tableBody.addEventListener('scroll', handleScroll);

    return () => {
      tableBody.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <div
        {...getTableProps()}
        className={classNames('virtual-table', tableClassNames)}
      >
        {headerGroups.map((headerGroup) => (
          <div
            {...headerGroup.getHeaderGroupProps()}
            className={classNames(
              'virtual-table__tr virtual-table__head virtual-table__tr--is-sticky',
              {
                'virtual-table__tr--has-padding':
                  tableHasScroll && disableWindowScroll,
              },
            )}
          >
            {headerGroup.headers.map((column) => (
              <div
                {...column.getHeaderProps([
                  {
                    className: column.className,
                    style: getColumnStyle(column),
                  },
                  column.getSortByToggleProps(),
                ])}
                className="virtual-table__th"
              >
                {column.render('Header')}
                {column.isSorted ? (
                  column.isSortedDesc ? (
                    <Icon name="sort down" color="teal" corner="top right" />
                  ) : (
                    <Icon name="sort up" color="teal" />
                  )
                ) : column.canSort ? (
                  <Icon name="sort" />
                ) : null}
              </div>
            ))}
          </div>
        ))}

        <div {...getTableBodyProps()}>
          <ReactWindowScroller>
            {({ ref: windowScrollerRef, outerRef, style, onScroll }) => (
              <InfiniteLoader
                isItemLoaded={(index) => index < data.length}
                itemCount={itemCount}
                loadMoreItems={onLoadMoreItems}
                threshold={8}
              >
                {({ onItemsRendered, ref: infiniteLoaderRef }) => {
                  return (
                    <FixedSizeList
                      className="virtual-table__body-rows"
                      ref={mergeRefs([windowScrollerRef, infiniteLoaderRef])}
                      outerRef={outerRef}
                      style={style}
                      height={window.innerHeight}
                      onScroll={(event) => {
                        window.dispatchEvent(new CustomEvent('scroll'));
                        onScroll(event);
                      }}
                      itemCount={itemCount}
                      itemSize={itemSize}
                      overscanCount={4}
                      onItemsRendered={onItemsRendered}
                    >
                      {renderRow}
                    </FixedSizeList>
                  );
                }}
              </InfiniteLoader>
            )}
          </ReactWindowScroller>
        </div>
      </div>
      {loading && (
        <Loader active inline="centered">
          Cargando…
        </Loader>
      )}
    </>
  );
}
